import Clearbit from '@clearkit/icons/identity/Clearbit';
import { CKButton } from '@clearkit/react';
import Radio from '@material-ui/core/Radio';
import ToggleButton from '@material-ui/lab/ToggleButton';

import Tile from '~/components/audiences/Tile/Prospects.svg';
import ContentBox from '~/components/ContentBox';
import SearchBuilder from '~/components/SearchBuilder';
import { EMPTY_STATE_SEARCH } from '~/components/SearchBuilder/utils/constants';
import { isValidSearch } from '~/components/SearchBuilder/utils/validateSearch';
import TextField from '~/components/TextField';
import ToggleRadioButtonGroup from '~/components/ToggleRadioButtonGroup';
import SegmentTile from '~/images/Tiles/Companies.svg';
import PeopleTile from '~/images/Tiles/People.svg';
import { audienceSubtypes } from '~/lib/audiences';
import { hasError } from '~/lib/graphql';
import history from '~/lib/history';
import SegmentForm from '~/pages/audiences/components/SegmentForm';
import RadioChecked from '~/pages/destinations/components/TriggerType/RadioChecked.svg';
import RadioUnchecked from '~/pages/destinations/components/TriggerType/RadioUnchecked.svg';
import paths from '~lib/paths';

import CompanySegment from './CompanySegment.svg';
import Segment from './Segment.svg';
import styles from './styles';
import {
  EMPTY_AUDIENCE_PERSON_ATTRIBUTE_SEARCH,
  EMPTY_AUDIENCE_PERSON_ATTRIBUTE_SEARCH_WITH_TITLE_KEYWORDS,
  filterPersonAttributes,
  normalizeSearch,
} from './utils';

const placeholderValue = '';

function AudiencesProspectsForm({
  account,
  classes,
  className,
  companyCollections,
  companySearchAttributes,
  disabled,
  errors,
  onChange,
  onValidate,
  prospectAudienceCompanySearchAttributes,
  prospectAudiencePersonSearchAttributes,
  values,
  values: {
    companySearch,
    externalName,
    name = placeholderValue,
    personSearch,
    segment,
  },
}) {
  const showSegmentPicker =
    values.subtype === audienceSubtypes.companySegmentProspects;
  const [showPreview, setShowPreview] = useState(false);
  const setShowSegmentPicker = (shouldShowSegmentPicker) => {
    const subtype = shouldShowSegmentPicker
      ? audienceSubtypes.companySegmentProspects
      : audienceSubtypes.prospects;
    history.push(paths.audienceNew(subtype));
  };

  const showToggle = !values.id;

  // This shows the hint at the top of the search builder to add the Title Keywords
  // attribute to an empty person search
  const showTitleHint = personSearch === EMPTY_AUDIENCE_PERSON_ATTRIBUTE_SEARCH;

  // Prioritize user input and fall back to audience name if possible
  const buildExternalName = (newName) =>
    externalName === values.name ? newName : externalName;

  const validation = {
    companySearch: !!segment || isValidSearch(companySearch),
    externalName: !!externalName,
    name: !!name,
    personSearch: !personSearch || isValidSearch(personSearch),
  };

  useEffect(() => {
    onValidate(validation);
  }, Object.values(validation));

  return (
    <ContentBox className={className}>
      {showToggle && (
        <ToggleRadioButtonGroup
          exclusive
          classes={{ root: classes.segmentToggleGroup }}
          selected
          onChange={(event, shouldShowSegmentPicker) => {
            if (!shouldShowSegmentPicker) {
              onChange({ segment: undefined });
            } else {
              onChange({ personSearch: undefined, companySearch: undefined });
            }
            setShowSegmentPicker(shouldShowSegmentPicker);
          }}>
          <ToggleButton
            className={classes.segmentToggle}
            value={false}
            selected={!showSegmentPicker}>
            <div>
              <p>
                <Radio
                  classes={{ root: classes.radioRoot }}
                  checked={!showSegmentPicker}
                  checkedIcon={<RadioChecked />}
                  icon={<RadioUnchecked />}
                />
                <Clearbit width={24} height={24} />
                Prospect from companies in Clearbit's entire dataset{' '}
              </p>
              <p>
                Create an audience of your ideal prospects from Clearbit's
                complete database of people and companies.
              </p>
            </div>
          </ToggleButton>
          <ToggleButton
            className={classes.segmentToggle}
            value
            selected={showSegmentPicker}>
            <div>
              <p>
                <Radio
                  classes={{ root: classes.radioRoot }}
                  checked={showSegmentPicker}
                  checkedIcon={<RadioChecked />}
                  icon={<RadioUnchecked />}
                />
                <CompanySegment width={24} height={24} />
                Prospect from an existing company segment
              </p>
              <p>
                Create a dynamic audience of prospects that updates every time a
                company enters or exits one of your segments.
              </p>
            </div>
          </ToggleButton>
        </ToggleRadioButtonGroup>
      )}
      {showSegmentPicker ? (
        <ContentBox.Group>
          <SegmentForm
            disabled={disabled}
            title={
              segment?.id && (
                <>
                  <ContentBox.Icon>
                    <SegmentTile className={classes.sectionTile} />
                  </ContentBox.Icon>
                  <p>
                    For{' '}
                    <strong>
                      every company in the segment{!segment?.id && '...'}
                    </strong>
                    {segment?.id && (
                      <div className={classes.pill}>
                        <Segment />
                        {segment.name}
                      </div>
                    )}
                  </p>
                </>
              )
            }
            actions={
              segment?.id && (
                <CKButton
                  onClick={() => {
                    setShowPreview(!showPreview);
                  }}
                  className={classes.previewButton}>
                  {showPreview ? 'Hide' : 'Show'} preview
                </CKButton>
              )
            }
            showPreview={showPreview || !segment?.id}
            companyCollections={companyCollections}
            companySearchAttributes={companySearchAttributes}
            values={{ segment }}
            onValidate={() => {}}
            onChange={({ segment }) => {
              setShowSegmentPicker(true);
              onChange({ segment });
            }}
          />
        </ContentBox.Group>
      ) : (
        <>
          <ContentBox.Group>
            <ContentBox.Section variant="horizontal">
              <ContentBox.Icon>
                <SegmentTile className={classes.sectionTile} />
              </ContentBox.Icon>
              <p>
                For <strong>every company</strong> matching...
              </p>
            </ContentBox.Section>

            <ContentBox.Section rel="company-builder">
              <SearchBuilder
                attributes={prospectAudienceCompanySearchAttributes}
                onChange={(newCompanySearch) => {
                  onChange({ companySearch: newCompanySearch });
                }}
                required
                search={companySearch || EMPTY_STATE_SEARCH}
                showInvalidFields={hasError(errors, 'companySearch')}
                type="Company"
              />
            </ContentBox.Section>
          </ContentBox.Group>
        </>
      )}

      <ContentBox.Group>
        {personSearch ? (
          <>
            <ContentBox.Section variant="horizontal">
              <ContentBox.Icon>
                <PeopleTile className={classes.sectionTile} />
              </ContentBox.Icon>
              <p>
                <strong>Prospect for people</strong> matching...
              </p>
            </ContentBox.Section>

            {showTitleHint && (
              <ContentBox.Section className={classes.hint}>
                Hint: Prospect Audiences generally perform best with larger
                audiences, but if you need to get more specific, try{' '}
                <a
                  role="button"
                  tabIndex="0"
                  onClick={() => {
                    onChange({
                      personSearch: EMPTY_AUDIENCE_PERSON_ATTRIBUTE_SEARCH_WITH_TITLE_KEYWORDS,
                    });
                  }}>
                  adding a Title Keyword to your search
                </a>
                .
              </ContentBox.Section>
            )}

            <ContentBox.Section rel="person-builder">
              <SearchBuilder
                attributes={filterPersonAttributes(
                  account,
                  prospectAudiencePersonSearchAttributes,
                )}
                onChange={(newPersonSearch) => {
                  newPersonSearch = normalizeSearch(newPersonSearch);
                  onChange({ personSearch: newPersonSearch });
                }}
                search={personSearch}
                showInvalidFields={hasError(errors, 'personSearch')}
                type="Person"
              />
            </ContentBox.Section>
          </>
        ) : (
          <ContentBox.Section variant="horizontal">
            <ContentBox.Icon>
              <PeopleTile className={classes.sectionTile} />
            </ContentBox.Icon>
            <span>
              <strong>Prospect for people</strong> with{' '}
              <strong>any role, seniority, or location</strong>
            </span>

            <CKButton
              className={classes.sectionAction}
              onClick={() => {
                onChange({
                  personSearch: EMPTY_AUDIENCE_PERSON_ATTRIBUTE_SEARCH,
                });
              }}>
              Add search criteria
            </CKButton>
          </ContentBox.Section>
        )}
      </ContentBox.Group>

      <ContentBox.Break />
      <ContentBox.Group>
        <ContentBox.Section>
          <p>
            Name this <strong>prospect audience</strong>
          </p>
          <TextField
            className={classes.field}
            error={hasError(errors, 'name')}
            fullWidth
            onChange={(event) => {
              const newName = event.target.value;
              const newExternalName = buildExternalName(newName);

              onChange({
                externalName: newExternalName,
                name: newName,
              });
            }}
            placeholder="Example: Front-end engineers"
            startAdornment={
              <Tile width="24" height="24" className={classes.fieldAdornment} />
            }
            required
            value={name}
          />
          <div className={classes.requiredLabel}>Required</div>
        </ContentBox.Section>
      </ContentBox.Group>
    </ContentBox>
  );
}

AudiencesProspectsForm.propTypes = {
  account: PropTypes.object,
  classes: PropTypes.object.isRequired,
  companyCollections: PropTypes.array.isRequired,
  companySearchAttributes: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
  errors: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
  prospectAudienceCompanySearchAttributes: PropTypes.array.isRequired,
  prospectAudiencePersonSearchAttributes: PropTypes.array.isRequired,
  values: PropTypes.object.isRequired,
};

export default withStyles(styles)(AudiencesProspectsForm);
